/**
 * K3 Deferred Tax Section
 *
 * Uppskjuten skatt enligt K3 kapitel 29
 * Obligatoriskt för K3-företag med temporära skillnader
 */

import { useState, useEffect } from "react";
import {
  Calculator,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import { formatNumber } from "@/lib/format";
import { reportsApi, type DeferredTaxCalculation, type AnnualReport } from "@/lib/api";
import { toast } from "sonner";

interface K3DeferredTaxSectionProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
}

export function K3DeferredTaxSection({ report, onUpdate }: K3DeferredTaxSectionProps) {
  const [deferredTax, setDeferredTax] = useState<DeferredTaxCalculation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // User adjustments
  const [taxLossRecognized, setTaxLossRecognized] = useState(true);
  const [manualAdjustmentLiability, setManualAdjustmentLiability] = useState(0);
  const [manualAdjustmentAsset, setManualAdjustmentAsset] = useState(0);

  const loadDeferredTax = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await reportsApi.getDeferredTax(report.id);
      if (response.success && response.data) {
        setDeferredTax(response.data.deferredTax);
        // Initialize user state from loaded data
        if (response.data.deferredTax) {
          setTaxLossRecognized(
            response.data.deferredTax.temporaryDifferencesAsset?.taxLossCarryforwardsRecognized ?? true
          );
        }
      } else {
        setError(response.error?.message || "Kunde inte beräkna uppskjuten skatt");
      }
    } catch (err) {
      console.error("Load deferred tax error:", err);
      setError("Ett fel uppstod vid beräkning av uppskjuten skatt");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDeferredTax();
  }, [report.id]);

  const handleSave = async () => {
    if (!deferredTax) return;

    setIsSaving(true);
    try {
      const updates: Partial<DeferredTaxCalculation> = {
        temporaryDifferencesAsset: {
          ...deferredTax.temporaryDifferencesAsset,
          taxLossCarryforwardsRecognized: taxLossRecognized,
        },
        specification: {
          ...deferredTax.specification,
          manualAdjustments: [
            ...(manualAdjustmentLiability !== 0
              ? [{ description: "Manuell justering skuld", amount: manualAdjustmentLiability, type: "liability" as const }]
              : []),
            ...(manualAdjustmentAsset !== 0
              ? [{ description: "Manuell justering fordran", amount: manualAdjustmentAsset, type: "asset" as const }]
              : []),
          ],
        },
        userAdjusted: true,
      };

      const response = await reportsApi.updateDeferredTax(report.id, updates);
      if (response.success && response.data) {
        setDeferredTax(response.data.deferredTax);
        onUpdate(response.data.report);
        toast.success("Uppskjuten skatt sparad");
      } else {
        toast.error(response.error?.message || "Kunde inte spara");
      }
    } catch (err) {
      console.error("Save deferred tax error:", err);
      toast.error("Ett fel uppstod vid sparning");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-gray-400 mr-2" size={20} />
          <span className="text-gray-500">Beräknar uppskjuten skatt...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50 border-red-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-500" size={20} />
            <div>
              <h3 className="font-semibold text-red-900">Fel vid beräkning</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
          <button onClick={loadDeferredTax} className="btn btn-outline text-sm">
            <RefreshCw size={16} className="mr-1" />
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (!deferredTax) {
    return null;
  }

  const hasLiability = deferredTax.deferredTaxLiability > 0;
  const hasAsset = deferredTax.deferredTaxAsset > 0;
  const hasTaxLoss = (deferredTax.temporaryDifferencesAsset?.taxLossCarryforwards ?? 0) > 0;

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <Calculator className="text-violet-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Uppskjuten skatt</h3>
            <p className="text-sm text-gray-500">K3 kapitel 29 - Obligatoriskt</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          K3
        </span>
      </div>

      {/* Info box */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info className="text-violet-500 mt-0.5 flex-shrink-0" size={16} />
          <p className="text-sm text-violet-800">
            K3-företag ska redovisa uppskjuten skatt på temporära skillnader mellan redovisat och
            skattemässigt värde. Skattesats: <strong>{(deferredTax.taxRate * 100).toFixed(1)}%</strong>
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Deferred Tax Liability */}
        <div className={`p-4 rounded-lg border ${hasLiability ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Uppskjuten skatteskuld</p>
          <p className={`text-xl font-bold ${hasLiability ? "text-red-700" : "text-gray-400"}`}>
            {formatNumber(deferredTax.deferredTaxLiability)} kr
          </p>
        </div>

        {/* Deferred Tax Asset */}
        <div className={`p-4 rounded-lg border ${hasAsset ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Uppskjuten skattefordran</p>
          <p className={`text-xl font-bold ${hasAsset ? "text-green-700" : "text-gray-400"}`}>
            {formatNumber(deferredTax.deferredTaxAsset)} kr
          </p>
        </div>

        {/* Net */}
        <div className={`p-4 rounded-lg border ${
          deferredTax.netDeferredTax > 0
            ? "bg-red-50 border-red-200"
            : deferredTax.netDeferredTax < 0
              ? "bg-green-50 border-green-200"
              : "bg-gray-50 border-gray-200"
        }`}>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Netto (skuld +)</p>
          <p className={`text-xl font-bold ${
            deferredTax.netDeferredTax > 0
              ? "text-red-700"
              : deferredTax.netDeferredTax < 0
                ? "text-green-700"
                : "text-gray-400"
          }`}>
            {formatNumber(deferredTax.netDeferredTax)} kr
          </p>
        </div>
      </div>

      {/* Change during year */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
        <span className="text-sm text-gray-600">Förändring under året (påverkar resultaträkningen)</span>
        <span className={`font-semibold ${deferredTax.changeInDeferredTax >= 0 ? "text-red-600" : "text-green-600"}`}>
          {deferredTax.changeInDeferredTax >= 0 ? "+" : ""}{formatNumber(deferredTax.changeInDeferredTax)} kr
        </span>
      </div>

      {/* Tax loss carryforward option */}
      {hasTaxLoss && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 mb-2">
                Underskottsavdrag: {formatNumber(deferredTax.temporaryDifferencesAsset?.taxLossCarryforwards ?? 0)} kr
              </p>
              <p className="text-xs text-amber-700 mb-3">
                Enligt K3 29.26 ska uppskjuten skattefordran för underskottsavdrag endast redovisas
                i den mån det är sannolikt att avdraget kan utnyttjas mot framtida skattepliktiga överskott.
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={taxLossRecognized}
                  onChange={(e) => setTaxLossRecognized(e.target.checked)}
                  className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <span className="text-sm text-amber-800">
                  Redovisa uppskjuten skattefordran för underskottsavdrag
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Details toggle */}
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        {showDetails ? "Dölj specifikation" : "Visa specifikation"}
      </button>

      {/* Details */}
      {showDetails && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          {/* Liability breakdown */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Temporära skillnader (skuld)</h4>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Obeskattade reserver</span>
                <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesLiability?.untaxedReserves ?? 0)} kr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Periodiseringsfonder</span>
                <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesLiability?.periodizationFunds ?? 0)} kr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Överavskrivningar</span>
                <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesLiability?.acceleratedDepreciation ?? 0)} kr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Uppskrivningar</span>
                <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesLiability?.revaluations ?? 0)} kr</span>
              </div>
              {(deferredTax.temporaryDifferencesLiability?.otherLiabilities ?? 0) !== 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Övriga</span>
                  <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesLiability?.otherLiabilities ?? 0)} kr</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200 font-medium">
                <span>Summa temporära skillnader (skuld)</span>
                <span>
                  {formatNumber(
                    (deferredTax.temporaryDifferencesLiability?.untaxedReserves ?? 0) +
                    (deferredTax.temporaryDifferencesLiability?.periodizationFunds ?? 0) +
                    (deferredTax.temporaryDifferencesLiability?.acceleratedDepreciation ?? 0) +
                    (deferredTax.temporaryDifferencesLiability?.revaluations ?? 0) +
                    (deferredTax.temporaryDifferencesLiability?.otherLiabilities ?? 0)
                  )} kr
                </span>
              </div>
              <div className="flex justify-between text-red-700 font-semibold">
                <span>Uppskjuten skatteskuld ({(deferredTax.taxRate * 100).toFixed(1)}%)</span>
                <span>{formatNumber(deferredTax.deferredTaxLiability)} kr</span>
              </div>
            </div>
          </div>

          {/* Asset breakdown */}
          {hasAsset && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Temporära skillnader (fordran)</h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ej avdragsgilla avsättningar</span>
                  <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesAsset?.nonDeductibleProvisions ?? 0)} kr</span>
                </div>
                {hasTaxLoss && taxLossRecognized && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Underskottsavdrag</span>
                    <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesAsset?.taxLossCarryforwards ?? 0)} kr</span>
                  </div>
                )}
                {(deferredTax.temporaryDifferencesAsset?.otherAssets ?? 0) !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Övriga</span>
                    <span className="font-medium">{formatNumber(deferredTax.temporaryDifferencesAsset?.otherAssets ?? 0)} kr</span>
                  </div>
                )}
                <div className="flex justify-between text-green-700 font-semibold pt-2 border-t border-gray-200">
                  <span>Uppskjuten skattefordran ({(deferredTax.taxRate * 100).toFixed(1)}%)</span>
                  <span>{formatNumber(deferredTax.deferredTaxAsset)} kr</span>
                </div>
              </div>
            </div>
          )}

          {/* Manual adjustments */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Manuella justeringar</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Justering skuld (+/-)</label>
                <input
                  type="number"
                  value={manualAdjustmentLiability}
                  onChange={(e) => setManualAdjustmentLiability(Number(e.target.value) || 0)}
                  className="input w-full"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Justering fordran (+/-)</label>
                <input
                  type="number"
                  value={manualAdjustmentAsset}
                  onChange={(e) => setManualAdjustmentAsset(Number(e.target.value) || 0)}
                  className="input w-full"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save button */}
      <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-secondary flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              Sparar...
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              Spara uppskjuten skatt
            </>
          )}
        </button>
      </div>
    </div>
  );
}
